import { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  Badge,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react'
import { Plus, PencilSimple, Trash, List } from '@phosphor-icons/react'
import api from '../../services/api'
import { Sidebar } from '../../layouts/DefaultLayout'
import socketService, { SOCKET_EVENTS } from '../../services/socket'

interface TicketCategory {
  id: number
  name: string
  description: string | null
  color: string
  isActive: boolean
  ticketCount: number
}

export default function CategoriesManagement() {
  const [categories, setCategories] = useState<TicketCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<TicketCategory | null>(null)
  const [formData, setFormData] = useState({ name: '', description: '', color: '#ECC94B' })
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const fetchInternalTicketsCount = async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [])

  const fetchCategories = async () => {
    setIsLoading(true)
    try {
      const res = await api.get('/internal-ticket-categories')
      setCategories(res.data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
      toast({
        title: 'Erro ao carregar categorias',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  const handleCreate = () => {
    setSelectedCategory(null)
    setFormData({ name: '', description: '', color: '#ECC94B' })
    onFormOpen()
  }

  const handleEdit = (category: TicketCategory) => {
    setSelectedCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#ECC94B',
    })
    onFormOpen()
  }

  const handleDelete = (category: TicketCategory) => {
    setSelectedCategory(category)
    onDeleteOpen()
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nome é obrigatório', status: 'warning', duration: 3000 })
      return
    }

    try {
      if (selectedCategory) {
        await api.put(`/internal-ticket-categories/${selectedCategory.id}`, formData)
        toast({ title: 'Categoria atualizada', status: 'success', duration: 3000 })
      } else {
        await api.post('/internal-ticket-categories', formData)
        toast({ title: 'Categoria criada', status: 'success', duration: 3000 })
      }
      onFormClose()
      fetchCategories()
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return

    try {
      await api.delete(`/internal-ticket-categories/${selectedCategory.id}`)
      toast({ title: 'Categoria excluída', status: 'success', duration: 3000 })
      onDeleteClose()
      fetchCategories()
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Box p={{ base: 3, md: 6 }} h="100%" overflowY="auto">
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <HStack justify="space-between" spacing={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <HStack spacing={3} align="center" flex={1} minW={0}>
              {isMobile && (
                <IconButton
                  aria-label="Menu"
                  icon={<List size={20} />}
                  variant="ghost"
                  onClick={onMenuOpen}
                  size="sm"
                  color={menuIconColor}
                  _hover={{ bg: borderColor }}
                  flexShrink={0}
                />
              )}
              <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color={textPrimary}>
                Categorias de Tickets Internos
              </Text>
            </HStack>
            <Button
              leftIcon={<Plus size={18} />}
              colorScheme="yellow"
              onClick={handleCreate}
              size={{ base: 'sm', md: 'md' }}
              flexShrink={0}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Nova Categoria</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Nova</Text>
            </Button>
          </HStack>

        {isLoading ? (
          <Spinner size="xl" color="yellow.500" alignSelf="center" />
        ) : isMobile ? (
          <VStack spacing={3} align="stretch">
            {categories.length === 0 ? (
              <Box
                bg={surfaceBg}
                p={8}
                borderRadius="lg"
                border="1px"
                borderColor={borderColor}
                textAlign="center"
              >
                <Text color={textMuted}>Nenhuma categoria encontrada</Text>
              </Box>
            ) : (
              categories.map((category) => (
                <Box
                  key={category.id}
                  bg={surfaceBg}
                  p={4}
                  borderRadius="lg"
                  border="1px"
                  borderColor={borderColor}
                >
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between" align="start">
                      <Text color={textPrimary} fontWeight="semibold" fontSize="sm" flex={1} minW={0}>
                        {category.name}
                      </Text>
                      <HStack spacing={1} flexShrink={0}>
                        <IconButton
                          aria-label="Editar"
                          icon={<PencilSimple size={16} />}
                          size="xs"
                          variant="ghost"
                          onClick={() => handleEdit(category)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<Trash size={16} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(category)}
                        />
                      </HStack>
                    </HStack>
                    <HStack spacing={2} align="center">
                      <Box
                        w="16px"
                        h="16px"
                        borderRadius="sm"
                        bg={category.color}
                        border="1px"
                        borderColor={borderColor}
                        flexShrink={0}
                      />
                      <Text fontSize="xs" color={textSecondary} flex={1} minW={0} noOfLines={1}>
                        {category.color}
                      </Text>
                    </HStack>
                    {category.description && (
                      <Text fontSize="xs" color={textMuted} noOfLines={2}>
                        {category.description}
                      </Text>
                    )}
                    <HStack justify="space-between" align="center">
                      <Text fontSize="xs" color={textMuted}>
                        Tickets:
                      </Text>
                      <Badge colorScheme="blue" fontSize="xs">
                        {category.ticketCount || 0}
                      </Badge>
                    </HStack>
                  </VStack>
                </Box>
              ))
            )}
          </VStack>
        ) : (
          <Box bg={surfaceBg} borderRadius="xl" overflow="hidden" border="1px" borderColor={borderColor}>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={textMuted}>Nome</Th>
                  <Th color={textMuted}>Cor</Th>
                  <Th color={textMuted}>Descrição</Th>
                  <Th color={textMuted} isNumeric>Tickets</Th>
                  <Th color={textMuted} w="120px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categories.length === 0 ? (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={8}>
                      <Text color={textMuted}>Nenhuma categoria encontrada</Text>
                    </Td>
                  </Tr>
                ) : (
                  categories.map((category) => (
                    <Tr key={category.id}>
                      <Td>
                        <Text fontWeight="medium" color={textPrimary}>
                          {category.name}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Box
                            w="20px"
                            h="20px"
                            borderRadius="md"
                            bg={category.color}
                            border="1px"
                            borderColor={borderColor}
                          />
                          <Text fontSize="sm" color={textSecondary}>
                            {category.color}
                          </Text>
                        </HStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color={textSecondary} noOfLines={1}>
                          {category.description || '-'}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Badge colorScheme="blue">{category.ticketCount || 0}</Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            aria-label="Editar"
                            icon={<PencilSimple size={18} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEdit(category)}
                          />
                          <IconButton
                            aria-label="Excluir"
                            icon={<Trash size={18} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(category)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size={{ base: 'full', sm: 'md' }}>
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader color={textPrimary}>
            {selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color={textSecondary}>Nome</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome da categoria"
                  bg={useColorModeValue('white', 'gray.700')}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textSecondary}>Descrição</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição (opcional)"
                  bg={useColorModeValue('white', 'gray.700')}
                  rows={3}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textSecondary}>Cor</FormLabel>
                <HStack spacing={3}>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    w="60px"
                    h="40px"
                    p={1}
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#ECC94B"
                    bg={useColorModeValue('white', 'gray.700')}
                  />
                </HStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSubmit}>
              {selectedCategory ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={surfaceBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textPrimary}>
              Excluir Categoria
            </AlertDialogHeader>
            <AlertDialogBody color={textSecondary}>
              Tem certeza que deseja excluir a categoria "{selectedCategory?.name}"?
              {selectedCategory && selectedCategory.ticketCount > 0 && (
                <Text mt={2} fontSize="sm" color="orange.400">
                  Esta categoria possui {selectedCategory.ticketCount} ticket(s). Os tickets terão a categoria removida.
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      </Box>
    </>
  )
}
